/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Accordion, Button, Card, Col, Row } from "react-bootstrap";

import { IAnyOtherAssets } from "../../../../config/interfaces";
import FASvgIcon from "../../../FASvgIcon";
import PreventEditComponent from "../../../PreventEditComponent";
import PriceDisplay from "../../../PriceDisplay";
import { SwitchBtnContextAwareToggle } from "../../../accordion";
import { FormField, NumericInputField, TextInputField } from "../../../fields";

const OAAssetExpensesIfAnyForm: React.FC = () => {
  const { values, handleBlur, setFieldValue, errors, touched } =
    useFormikContext<IAnyOtherAssets>();

  const [sum, setSum] = useState(0);
  const [inputTotal, setInputTotal] = useState<{ [key: string]: number }>({});
  const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());

  useEffect(() => {
    let _sum = 0;
    let _totalInput: { [key: string]: number } = {};
    if (values?.ImprovementExpenses.length) {
      values?.ImprovementExpenses.forEach((item, index) => {
        _totalInput[index] = parseInt(item[1]) || 0;
        if (item.length && item[1]) {
          _sum += parseInt(item[1]) || 0;
        }
      });
      setInputTotal(() => _totalInput);
    }
    setSum(() => _sum);
  }, [currentTime]);

  const handleAmountInc = (amt: number, index: string) => {
    inputTotal[index] = amt;
    setInputTotal(() => inputTotal);
    const _sum = Object.values(inputTotal).reduce((a, b) => Number(a) + Number(b), 0);
    setSum(() => _sum);
  };

  return (
    <>
      <div className="section-title-wrap">
        <h5>Expenses, if any</h5>
      </div>
      <FormField label="Transfer expenses" sm={4}>
        <NumericInputField
          showLeftIcon={true}
          name="TransferExpense"
          value={values.TransferExpense}
          onBlur={handleBlur("TransferExpense")}
          onChange={(value) => setFieldValue("TransferExpense", value)}
          isInvalid={Boolean(errors.TransferExpense) && touched.TransferExpense}
          errorMsg={errors.TransferExpense}
          note="Example: Brokerage fees on sale of shares"
        />
      </FormField>
      <Accordion flush activeKey={values.AddCostOfImprovementExpanses ? "0" : "1"}>
        <Card className="more-info-card shadow-none mb-5 overflow-hidden">
          <Card.Header className={classNames(!values?.AddCostOfImprovementExpanses && "border-0")}>
            <Card.Title>
              Add cost of improvement expenses?
              <SwitchBtnContextAwareToggle
                eventKey="0"
                name="AddCostOfImprovementExpanses"
                onChange={(checked: boolean) =>
                  setFieldValue("AddCostOfImprovementExpanses", checked)
                }
              />
            </Card.Title>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            <>
              <Card.Body>
                <FieldArray name="ImprovementExpenses">
                  {({ push, remove, replace, form }) => (
                    <>
                      {values?.ImprovementExpenses?.length > 0
                        ? values.ImprovementExpenses.map((_, index) => (
                            <section className="mb-3 mb-sm-0" key={`improvement-expense-${index}`}>
                              <ExpensesForm
                                index={index}
                                form={form}
                                onRemove={(itemIdx: number) => {
                                  if (values.ImprovementExpenses.length === 1) {
                                    replace(itemIdx, ["", ""]);
                                  } else {
                                    remove(itemIdx);
                                  }
                                  setCurrentTime(new Date().getTime());
                                }}
                                handleAmountInc={handleAmountInc}
                              />
                            </section>
                          ))
                        : null}
                      <PreventEditComponent>
                        <Button
                          variant="link"
                          className="btn-add-link"
                          onClick={() => push(["", ""])}
                        >
                          <FASvgIcon icon={faCirclePlus} />
                          Add more
                        </Button>
                      </PreventEditComponent>
                    </>
                  )}
                </FieldArray>
              </Card.Body>
              <Card.Footer>
                <p className="total-content">
                  <span className="total-label">Total:</span>
                  <span className="total-amount">
                    <PriceDisplay price={sum} />
                  </span>
                </p>
              </Card.Footer>
            </>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  );
};

export default OAAssetExpensesIfAnyForm;

type FormProps = {
  index: number;
  form: FormikProps<IAnyOtherAssets>;
  onRemove: (itemIdx: number) => void;
  handleAmountInc: (amt: number, index: string) => void;
};

const ExpensesForm: React.FC<FormProps> = (props: FormProps) => {
  const { index, onRemove, form, handleAmountInc } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  return (
    <>
      <Row className="align-items-center">
        <Col sm>
          <TextInputField
            name={`ImprovementExpenses.${index}[0]`}
            label="Description"
            value={values.ImprovementExpenses[index][0] || ""}
            onChange={(value) => setFieldValue(`ImprovementExpenses.${index}[0]`, value)}
            onBlur={() => {
              handleBlur(`ImprovementExpenses.${index}[0]`);
              setFieldValue(
                `ImprovementExpenses.${index}[0]`,
                values.ImprovementExpenses[index][0].trim()
              );
            }}
            isInvalid={
              Boolean(getIn(errors, `ImprovementExpenses.${index}[0]`)) &&
              getIn(touched, `ImprovementExpenses.${index}[0]`)
            }
            errorMsg={getIn(errors, `ImprovementExpenses.${index}[0]`)}
          />
        </Col>
        <Col sm>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name={`ImprovementExpenses.${index}[1]`}
            label="Amount"
            value={values.ImprovementExpenses[index][1] || ""}
            onBlur={handleBlur(`ImprovementExpenses.${index}[1]`)}
            onChange={(value) => {
              setFieldValue(`ImprovementExpenses.${index}[1]`, value);
              setTimeout(() => {
                handleAmountInc(Number(value), String(index));
              }, 100);
            }}
            isInvalid={
              Boolean(getIn(errors, `ImprovementExpenses.${index}[1]`)) &&
              getIn(touched, `ImprovementExpenses.${index}[1]`)
            }
            errorMsg={getIn(errors, `ImprovementExpenses.${index}[1]`)}
          />
        </Col>
        <PreventEditComponent>
          <Col sm={"auto"} className="ps-0">
            <Button
              variant="transparent"
              className="text-greyDark icon-only d-none d-sm-flex"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
            </Button>
            <Button
              variant="transparent"
              className="btn-delete-full d-sm-none"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
              <span>Delete</span>
            </Button>
          </Col>
        </PreventEditComponent>
      </Row>
      {index !== values.ImprovementExpenses.length - 1 && <hr className="d-block d-sm-none my-4" />}
    </>
  );
};
